import './CopyText.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { CopyOutlined } from '@ant-design/icons';

import { Button, ButtonProps, Tooltip } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import { useBoolean, useClipboard, usePrevious } from '@hooks';

export const selectElementText = (element: HTMLElement | null) => {
	if (window.getSelection && element) {
		const selection = window.getSelection();
		const range = document.createRange();
		range.selectNodeContents(element);
		selection?.removeAllRanges();
		selection?.addRange(range);
	}
};

export type CopyTextProps = ButtonProps & {
	buttonOnly?: boolean;
	displayText?: string;
	className?: string;
	onClick?: () => void;
	text?: string;
	tooltip?: string;
};

export const CopyText: React.FC<CopyTextProps> = (props) => {
	const { buttonOnly, disabled, displayText, className, onClick, text, tooltip, ...rest } = props;
	const buttonRef = useRef<HTMLButtonElement>(null);
	const previousText = usePrevious(text);
	const [ copied, setCopied ] = useBoolean(false);
	const [ timedOut, setTimedOut ] = useBoolean(false);
	const clipboard = useClipboard();

	const loading = copied && !text;
	const failed = timedOut && !text;
	const highlight = !buttonOnly && !displayText;
	const hoverText = tooltip || COPY_SPECIFIC.CLICK_TO_COPY;
	const copyText = loading ? COPY_SPECIFIC.COPYING : `${COPY_SPECIFIC.COPIED}!`;
	const clickText = failed ? COPY_SPECIFIC.COPY_FAILED : copyText;
	const tooltipText = copied ? clickText : hoverText;
	const buttonText = displayText || text;

	useEffect(() => {
		if (text && text !== previousText) {
			clipboard.writeText(text);
		}
	}, [ clipboard, previousText, text ]);

	const handleOnClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();

			if (onClick) {
				onClick();
			}

			if (text) {
				clipboard.writeText(text);
			}

			setCopied.on();
			setTimedOut.off();
			selectElementText(buttonRef?.current);
			setTimeout(setTimedOut.on, 3000);
		},
		[ onClick, text, setCopied, setTimedOut, clipboard ]
	);

	const handleOnMouseLeave = useCallback(() => {
		buttonRef.current?.blur();
		setTimeout(setCopied.off, 500);
	}, [ setCopied.off ]);

	const copyTextClassNames = classNames('copy-text', className, {
		copied,
		failed,
		highlight
	});

	return (
		<Tooltip
			className={ copyTextClassNames }
			placement='bottom'
			title={ disabled ? undefined : tooltipText }>
			<Button
				disabled={ disabled }
				icon={ <CopyOutlined /> }
				loading={ loading }
				onClick={ handleOnClick }
				onMouseLeave={ handleOnMouseLeave }
				ref={ buttonRef }
				{ ...rest }>
				{ buttonOnly ? null : buttonText }
			</Button>
		</Tooltip>
	);
};
